import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  Output,
  ViewChild,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { CodeConfigFile } from "src/app/shared/interfaces/code-config-file";
import {
  ForumAttachment,
  ForumAttachmentConfig,
  ForumMember,
} from "src/app/shared/interfaces/forum";
import { FileSizePipe } from "src/app/shared/pipes/file-size.pipe";
import { SharedModule } from "src/app/shared/shared.module";
import { ForumMentionsListComponent } from "../forum-mentions-list/forum-mentions-list.component";
import { ForumEditorToolbarComponent } from "./forum-editor-toolbar/forum-editor-toolbar.component";
import {
  ForumEditorToolbarOption,
  ForumEditorToolbarOptionType,
} from "./forum-editor-toolbar/forum-editor-toolbar.types";
import { ForumMention } from "./forum-editor-config/forum-editor-mention.config";

type ForumEditorAttachmentItem = {
  filename: string;
  size?: number;
  source: "existing" | "new";
  index: number;
};

@Component({
  selector: "forum-editor",
  templateUrl: "./forum-editor.component.html",
  styleUrls: ["./forum-editor.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    SharedModule,
    ForumEditorToolbarComponent,
    ForumMentionsListComponent,
  ],
  standalone: true,
  providers: [
    FileSizePipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ForumEditorComponent),
      multi: true,
    },
  ],
})
export class ForumEditorComponent
  implements AfterViewInit, OnDestroy, OnChanges, ControlValueAccessor
{
  @ViewChild("editorWrapper") editorWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild("editorElement") editorElement!: ElementRef;
  @ViewChild("attachmentInput") attachmentInput!: ElementRef<HTMLInputElement>;
  @Input() content: string;
  @Input() members: ForumMember[] = [];
  @Input() existingAttachments: ForumAttachment[] = [];
  @Input() attachmentConfig: ForumAttachmentConfig | null = null;
  @Input() placeholder = "GROUPS.LIST.FORUM_EDITOR_PLACEHOLDER";
  @Output() removeExistingAttachment = new EventEmitter<number>();

  editor!: Editor;
  toolbarOptions: ForumEditorToolbarOption[] = [];
  selectedAttachments: CodeConfigFile[] = [];
  mentionCandidates: ForumMember[] = [];
  activeMentionIndex = 0;
  mentionDropdownPosition: { top: number; left: number } = {
    top: 0,
    left: 0,
  };
  attachmentError: string | null = null;
  isDragActive = false;
  private dragDepth = 0;
  private value = "";
  private isDisabled = false;
  private mentionRange: { from: number; to: number } | null = null;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly fileSizePipe: FileSizePipe,
  ) {
    this.toolbarOptions = this.buildToolbarOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["attachmentConfig"]) {
      this.toolbarOptions = this.buildToolbarOptions();
      this.cdr.markForCheck();
    }
  }

  private buildToolbarOptions(): ForumEditorToolbarOption[] {
    return [
      {
        type: ForumEditorToolbarOptionType.Action,
        label: "bold",
        actionFn: () => this.startBold(),
        isActiveFn: () => this.isBoldActive(),
      },
      {
        type: ForumEditorToolbarOptionType.Action,
        label: "italic",
        actionFn: () => this.startItalic(),
        isActiveFn: () => this.isItalicActive(),
      },
      {
        type: ForumEditorToolbarOptionType.Action,
        label: "strikethrough",
        actionFn: () => this.startStrike(),
        isActiveFn: () => this.isStrikeActive(),
      },
      {
        type: ForumEditorToolbarOptionType.Divider,
      },
      {
        type: ForumEditorToolbarOptionType.Action,
        label: "heading-1",
        actionFn: () => this.startHeading(1),
        isActiveFn: () => this.isHeadingActive(1),
      },
      {
        type: ForumEditorToolbarOptionType.Action,
        label: "heading-2",
        actionFn: () => this.startHeading(2),
        isActiveFn: () => this.isHeadingActive(2),
      },
      {
        type: ForumEditorToolbarOptionType.Action,
        label: "heading-3",
        actionFn: () => this.startHeading(3),
        isActiveFn: () => this.isHeadingActive(3),
      },
      {
        type: ForumEditorToolbarOptionType.Divider,
      },
      {
        type: ForumEditorToolbarOptionType.Action,
        label: "list",
        actionFn: () => this.startBulletList(),
        isActiveFn: () => this.isBulletListActive(),
      },
      {
        type: ForumEditorToolbarOptionType.Action,
        label: "list-ordered",
        actionFn: () => this.startOrderedList(),
        isActiveFn: () => this.isOrderedListActive(),
      },
      {
        type: ForumEditorToolbarOptionType.Action,
        label: "code",
        actionFn: () => this.startCodeBlock(),
        isActiveFn: () => this.isCodeBlockActive(),
      },
      {
        type: ForumEditorToolbarOptionType.Action,
        label: "paperclip",
        actionFn: () => this.openAttachmentPicker(),
        tooltipFn: () => this.attachmentTooltip,
      },
    ];
  }

  ngAfterViewInit(): void {
    this.editor = new Editor({
      element: this.editorElement.nativeElement,
      extensions: [StarterKit, ForumMention],
      content: this.content || this.value || "",
      editable: !this.isDisabled,
      editorProps: {
        attributes: {
          class: "forum-editor-content",
        },
        handleKeyDown: (_, event) => this.handleMentionKeyDown(event),
      },
    });

    this.editor.on("selectionUpdate", () => {
      this.syncMentionCandidates();
      this.cdr.markForCheck();
    });
    this.editor.on("update", () => {
      this.value = this.editor.getHTML();
      this.onChange(this.value);
      this.syncMentionCandidates();
      this.cdr.markForCheck();
    });
    this.editor.on("transaction", () => this.cdr.markForCheck());
    this.editor.on("blur", () => {
      this.value = this.editor.getHTML();
      this.onChange(this.value);
      this.onTouched();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
  }

  startBold(): void {
    if (this.hasTextSelection()) {
      this.editor.chain().focus().toggleBold().run();
      return;
    }

    if (this.isBoldActive()) {
      this.editor.chain().focus("end").unsetBold().run();
      return;
    }

    this.editor
      .chain()
      .focus("end")
      .insertContent({ type: "paragraph" })
      .setBold()
      .run();
  }

  startItalic(): void {
    if (this.hasTextSelection()) {
      this.editor.chain().focus().toggleItalic().run();
      return;
    }

    if (this.isItalicActive()) {
      this.editor.chain().focus("end").unsetItalic().run();
      return;
    }

    this.editor
      .chain()
      .focus("end")
      .insertContent({ type: "paragraph" })
      .setItalic()
      .run();
  }

  startStrike(): void {
    if (this.hasTextSelection()) {
      this.editor.chain().focus().toggleStrike().run();
      return;
    }

    if (this.isStrikeActive()) {
      this.editor.chain().focus("end").unsetStrike().run();
      return;
    }

    this.editor
      .chain()
      .focus("end")
      .insertContent({ type: "paragraph" })
      .setStrike()
      .run();
  }

  isBoldActive(): boolean {
    return this.editor?.isActive("bold") ?? false;
  }

  isItalicActive(): boolean {
    return this.editor?.isActive("italic") ?? false;
  }

  isStrikeActive(): boolean {
    return this.editor?.isActive("strike") ?? false;
  }

  private hasTextSelection(): boolean {
    return !this.editor.state.selection.empty;
  }

  startBulletList(): void {
    if (this.hasTextSelection()) {
      this.editor.chain().focus().toggleBulletList().run();
      return;
    }

    if (this.isBulletListActive()) {
      this.editor.chain().focus("end").setParagraph().run();
      return;
    }

    this.editor.chain().focus("end").toggleBulletList().run();
  }

  startOrderedList(): void {
    if (this.hasTextSelection()) {
      this.editor.chain().focus().toggleOrderedList().run();
      return;
    }

    if (this.isOrderedListActive()) {
      this.editor.chain().focus("end").setParagraph().run();
      return;
    }

    this.editor.chain().focus("end").toggleOrderedList().run();
  }

  startHeading(level: 1 | 2 | 3): void {
    if (this.hasTextSelection()) {
      this.editor.chain().focus().toggleHeading({ level }).run();
      return;
    }

    if (this.isHeadingActive(level)) {
      this.editor.chain().focus("end").setParagraph().run();
      return;
    }

    this.editor.chain().focus("end").setNode("heading", { level }).run();
  }

  startCodeBlock(): void {
    if (this.hasTextSelection()) {
      this.editor.chain().focus().toggleCodeBlock().run();
      return;
    }

    if (this.isCodeBlockActive()) {
      this.editor.chain().focus("end").setParagraph().run();
      return;
    }

    this.editor.chain().focus("end").toggleCodeBlock().run();
  }

  isHeadingActive(level: 1 | 2 | 3): boolean {
    return this.editor?.isActive("heading", { level }) ?? false;
  }

  isBulletListActive(): boolean {
    return this.editor?.isActive("bulletList") ?? false;
  }

  isOrderedListActive(): boolean {
    return this.editor?.isActive("orderedList") ?? false;
  }

  isCodeBlockActive(): boolean {
    return this.editor?.isActive("codeBlock") ?? false;
  }

  undo(): void {
    this.editor.chain().focus().undo().run();
  }

  redo(): void {
    this.editor.chain().focus().redo().run();
  }

  focus(): void {
    this.editor?.chain().focus().run();
  }

  get isEmpty(): boolean {
    return this.editor?.isEmpty ?? true;
  }

  get attachmentTooltip(): string | null {
    if (!this.attachmentConfig) {
      return null;
    }

    return [
      this.translate.instant("IDE.UPLOAD.MAX_FILE_SIZE", {
        maxFileSize: this.fileSizePipe.transform(
          this.attachmentConfig.max_size,
        ),
      }),
      `${this.translate.instant(
        "IDE.UPLOAD.ALLOWED_EXTENSIONS",
      )}: ${this.attachmentConfig.allowed_extensions
        .map((extension) => extension.toUpperCase())
        .join(", ")}`,
    ].join("\n");
  }

  get allAttachments(): ForumEditorAttachmentItem[] {
    const existingAttachments = this.existingAttachments.map(
      (attachment, index) => ({
        filename: attachment.filename,
        size: attachment.size,
        source: "existing" as const,
        index,
      }),
    );

    const selectedAttachments = this.selectedAttachments.map(
      (attachment, index) => ({
        filename: attachment.filename,
        size: attachment.content.size,
        source: "new" as const,
        index,
      }),
    );

    return [...existingAttachments, ...selectedAttachments];
  }

  openAttachmentPicker(): void {
    const input = this.attachmentInput?.nativeElement;

    if (!input) {
      return;
    }

    if ("showPicker" in input && typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }

    input.click();
  }

  handleDragEnter(event: DragEvent): void {
    if (!this.hasDraggedFiles(event)) {
      return;
    }

    event.preventDefault();
    this.dragDepth += 1;
    this.isDragActive = true;
    this.cdr.markForCheck();
  }

  handleDragOver(event: DragEvent): void {
    if (!this.hasDraggedFiles(event)) {
      return;
    }

    event.preventDefault();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
  }

  handleDragLeave(event: DragEvent): void {
    if (!this.hasDraggedFiles(event)) {
      return;
    }

    event.preventDefault();
    this.dragDepth = Math.max(0, this.dragDepth - 1);

    if (this.dragDepth === 0) {
      this.isDragActive = false;
      this.cdr.markForCheck();
    }
  }

  handleDrop(event: DragEvent): void {
    if (!this.hasDraggedFiles(event)) {
      return;
    }

    event.preventDefault();
    this.dragDepth = 0;
    this.isDragActive = false;
    this.cdr.markForCheck();
  }

  handleDraggedAttachmentsSelected(files: CodeConfigFile[]): void {
    this.dragDepth = 0;
    this.isDragActive = false;
    this.addAttachments(
      files
        .map((attachment) => attachment.content)
        .filter((file): file is File => file instanceof File),
    );
  }

  selectMention(member: ForumMember): void {
    if (!this.editor || !this.mentionRange) {
      return;
    }

    const mentionLabel = this.getMemberDisplayName(member);

    this.editor
      .chain()
      .focus()
      .deleteRange(this.mentionRange)
      .insertContent([
        {
          type: "mention",
          attrs: {
            id: member.id,
            label: mentionLabel,
          },
        },
        {
          type: "text",
          text: " ",
        },
      ])
      .run();

    this.closeMentionCandidates();
    this.value = this.editor.getHTML();
    this.onChange(this.value);
    this.cdr.markForCheck();
  }

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    this.addAttachments(files);

    input.value = "";
  }

  removeAttachment(index: number): void {
    this.selectedAttachments = this.selectedAttachments.filter(
      (_, itemIndex) => itemIndex !== index,
    );
  }

  handleRemoveExistingAttachment(index: number): void {
    this.removeExistingAttachment.emit(index);
  }

  handleRemoveAttachment(listIndex: number): void {
    const attachment = this.allAttachments[listIndex];

    if (!attachment) {
      return;
    }

    if (attachment.source === "existing") {
      this.handleRemoveExistingAttachment(attachment.index);
      return;
    }

    this.removeAttachment(attachment.index);
  }

  private addAttachments(files: File[]): void {
    if (!files.length) {
      return;
    }

    const validAttachments: CodeConfigFile[] = [];
    const errors: string[] = [];

    files.forEach((file) => {
      const validationError = this.validateAttachment(file);

      if (validationError) {
        errors.push(validationError);
        return;
      }

      validAttachments.push({
        filename: file.name,
        content: file,
      });
    });

    if (validAttachments.length) {
      this.selectedAttachments = [
        ...this.selectedAttachments,
        ...validAttachments,
      ];
    }

    this.attachmentError = errors[0] || null;
    this.cdr.markForCheck();
  }

  private validateAttachment(file: File): string | null {
    if (!this.attachmentConfig) {
      return null;
    }

    const extension = file.name.includes(".")
      ? file.name.split(".").pop()?.toLowerCase() || ""
      : "";

    if (
      extension &&
      !this.attachmentConfig.allowed_extensions.includes(extension)
    ) {
      return this.translate.instant(
        "GROUPS.LIST.FORUM_ATTACHMENT_INVALID_EXTENSION",
        {
          extension: `.${extension}`,
        },
      );
    }

    if (file.size > this.attachmentConfig.max_size) {
      return this.translate.instant(
        "GROUPS.LIST.FORUM_ATTACHMENT_MAX_SIZE_EXCEEDED",
        {
          maxFileSize: this.fileSizePipe.transform(
            this.attachmentConfig.max_size,
          ),
        },
      );
    }

    return null;
  }

  private hasDraggedFiles(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types || []).includes("Files");
  }

  getSelectedAttachments(): File[] {
    return this.selectedAttachments.map(
      (attachment) => attachment.content as File,
    );
  }

  getHtml(): string {
    return this.editor.getHTML();
  }

  getJson(): unknown {
    return this.editor.getJSON();
  }

  clearContent(): void {
    this.editor.commands.clearContent();
    this.value = "";
    this.onChange(this.value);
    this.selectedAttachments = [];
    this.cdr.markForCheck();
  }

  setContent(content: string): void {
    this.value = content || "";
    this.editor.commands.setContent(this.value);
    this.cdr.markForCheck();
  }

  writeValue(value: string | null): void {
    this.value = value || "";

    if (this.editor) {
      this.editor.commands.setContent(this.value);
      this.cdr.markForCheck();
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.editor?.setEditable(!isDisabled);
    this.cdr.markForCheck();
  }

  private syncMentionCandidates(): void {
    if (!this.editor || !this.members?.length) {
      this.closeMentionCandidates();
      return;
    }

    const selection = this.editor.state.selection;
    if (!selection.empty) {
      this.closeMentionCandidates();
      return;
    }

    const textBefore = this.editor.state.doc.textBetween(
      Math.max(0, selection.from - 60),
      selection.from,
      "\n",
      " ",
    );
    const match = textBefore.match(/(^|\s)@([^\s@]*)$/);

    if (!match) {
      this.closeMentionCandidates();
      return;
    }

    const query = (match[2] || "").toLowerCase();
    const filteredMembers = this.members.filter((member) =>
      this.getMemberSearchValue(member).includes(query),
    );

    if (!filteredMembers.length) {
      this.closeMentionCandidates();
      return;
    }

    this.mentionRange = {
      from: selection.from - query.length - 1,
      to: selection.from,
    };
    this.mentionCandidates = filteredMembers.slice(0, 6);
    this.activeMentionIndex = Math.min(
      this.activeMentionIndex,
      this.mentionCandidates.length - 1,
    );
    this.updateMentionDropdownPosition(selection.from);
  }

  private closeMentionCandidates(): void {
    this.mentionCandidates = [];
    this.activeMentionIndex = 0;
    this.mentionRange = null;
  }

  private handleMentionKeyDown(event: KeyboardEvent): boolean {
    if (!this.mentionCandidates.length) {
      return false;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.activeMentionIndex =
        (this.activeMentionIndex + 1) % this.mentionCandidates.length;
      this.cdr.markForCheck();
      return true;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      this.activeMentionIndex =
        (this.activeMentionIndex - 1 + this.mentionCandidates.length) %
        this.mentionCandidates.length;
      this.cdr.markForCheck();
      return true;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      this.selectMention(this.mentionCandidates[this.activeMentionIndex]);
      return true;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      this.closeMentionCandidates();
      this.cdr.markForCheck();
      return true;
    }

    return false;
  }

  private getMemberSearchValue(member: ForumMember): string {
    return [member.first_name || "", member.last_name || "", member.email || ""]
      .join(" ")
      .toLowerCase();
  }

  private getMemberDisplayName(member: ForumMember): string {
    return (
      [member.first_name, member.last_name].filter(Boolean).join(" ") ||
      member.email ||
      ""
    );
  }

  private updateMentionDropdownPosition(cursorPosition: number): void {
    if (!this.editor || !this.editorWrapper) {
      return;
    }

    const caretCoords = this.editor.view.coordsAtPos(cursorPosition);
    const wrapperRect =
      this.editorWrapper.nativeElement.getBoundingClientRect();
    const dropdownWidth = 320;
    const rowHeight = 48;
    const dropdownHeight =
      Math.min(this.mentionCandidates.length, 6) * rowHeight + 16;
    const verticalOffset = 8;
    const horizontalPadding = 12;

    const availableBelow = wrapperRect.bottom - caretCoords.bottom;
    const shouldOpenAbove = availableBelow < dropdownHeight + verticalOffset;

    const maxLeft = Math.max(
      horizontalPadding,
      wrapperRect.width - dropdownWidth - horizontalPadding,
    );
    const left = Math.min(
      Math.max(caretCoords.left - wrapperRect.left, horizontalPadding),
      maxLeft,
    );

    const top = shouldOpenAbove
      ? caretCoords.top - wrapperRect.top - dropdownHeight - verticalOffset
      : caretCoords.bottom - wrapperRect.top + verticalOffset;

    this.mentionDropdownPosition = {
      top: Math.max(top, horizontalPadding),
      left,
    };
  }
}
